import styled, { css } from 'styled-components/macro'
import { respondAbove } from 'styles/mediaQueries'
import Text from 'components/shared/Text'

interface FieldProps {
    isDisabled?: boolean
    isError?: boolean
    type: 'text' | 'email' | 'tel' | 'textarea' | 'password' | 'number'
    isCustomField?: boolean
    isLargePaddingRight?: boolean
    hasDarkMode?: boolean
}

export const Field = styled.input<FieldProps>`
    width: 100%;
    padding: ${({ isLargePaddingRight }) =>
        isLargePaddingRight ? '10px 75px 10px 20px' : '10px 20px'};
    font-size: ${({ theme }) => theme.textVariants.body.fontSizeMobile}px;
    line-height: ${({ theme }) => theme.textVariants.body.lineHeightMobile}px;
    font-family: ${({ theme }) => theme.textVariants.body.fontFamily};
    background-color: ${({ theme, hasDarkMode }) =>
        hasDarkMode ? 'transparent' : theme.colors.grayscale_0};
    color: ${({ theme, hasDarkMode }) =>
        hasDarkMode ? theme.colors.grayscale_0 : theme.colors.grayscale_900};
    border-radius: 6px;
    border: 2px solid
        ${({ theme, isError }) =>
            isError ? theme.colors.error : theme.colors.grayscale_400};
    transition: border-color 100ms ease-in;
    ${({ type }) =>
        type !== 'textarea' &&
        css`
            height: 44px;
        `};

    ${({ isCustomField, isDisabled }) =>
        !isCustomField &&
        css`
            opacity: ${isDisabled ? 0.5 : 1};
        `};

    ${({ theme }) => {
        if (theme.mode === 'dark') {
            return css`
                background-color: ${theme.colors.darkmode_500};
                border-color: ${theme.colors.grayscale_400} !important;
                color: #fff;
            `
        }
    }};

    &:focus {
        border-color: ${({ theme, isError }) =>
            isError ? theme.colors.error : theme.colors.secondary_500};
    }

    ::placeholder {
        color: ${({ theme }) => theme.colors.grayscale_700} !important;
    }

    & + ${Text}:hover {
        ${({ type }) =>
            type !== 'textarea' &&
            css`
                color: ${({ theme }) => theme.colors.grayscale_900};
            `};
    }

    @media ${respondAbove.xl} {
        font-size: ${({ theme }) => theme.textVariants.body.fontSizeDesktop}px;
        line-height: ${({ theme }) =>
            theme.textVariants.body.lineHeightDesktop}px;
        padding: ${({ isLargePaddingRight }) =>
            isLargePaddingRight ? '12px 75px 12px 20px' : '12px 20px'};
        border-radius: 8px;

        ${({ type }) =>
            type !== 'textarea' &&
            css`
                height: 48px;
            `};
    }
`

interface WrapperProps {
    hasNoMaxWidth?: boolean
    maxWidth?: number
    minHeight?: number
    hasDarkMode?: boolean
}

export const Wrapper = styled.div<WrapperProps>`
    position: relative;
    width: 100%;

    @media ${respondAbove.xl} {
        width: ${({ maxWidth }) => maxWidth && `${maxWidth}px`};
    }

    ${({ hasNoMaxWidth }) =>
        !hasNoMaxWidth &&
        css`
            max-width: 370px;

            @media ${respondAbove.xl} {
                min-width: 180px;
            }
        `}

    & > * {
        cursor: pointer;
    }

    * + * {
        position: absolute;
        right: 20px;
        height: 100%;
        top: 0;
        display: flex;
        align-items: center;
        cursor: pointer;
        user-select: none;
    }

    & + ${Text} {
        @media ${respondAbove.xl} {
            max-width: 370px;
            width: 100%;
        }
    }

    &:focus-visible {
        ${Field} {
            border-color: ${({ theme, hasDarkMode }) =>
                hasDarkMode
                    ? theme.colors.primary_700
                    : theme.colors.secondary_500};
        }
    }

    input[type='number'] {
        -moz-appearance: textfield;
        ::-webkit-outer-spin-button,
        ::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }

    textarea {
        position: initial;
        min-height: ${({ theme, minHeight }) =>
            minHeight ? minHeight : 180}px;
        resize: vertical;
        padding-bottom: 36px;
    }
`

export const Suffix = styled(Text)`
    position: absolute;
    right: 20px;
`

export const Counter = styled(Text)`
    position: absolute;
    top: initial;
    height: 36px;
    bottom: 6px;
    right: 23px;
    background-color: ${({ theme }) => theme.colors.grayscale_0};
    width: 90%;
    display: block;
    text-align: right;

    @media ${respondAbove.xs} {
        width: 92%;
    }
    @media ${respondAbove.sm} {
        width: 94%;
    }
    @media ${respondAbove.xl} {
        width: 96%;
    }
`

export const EventInputWrapper = styled.div`
    position: relative;
    width: 100%;
`

export const EventTrigger = styled.div`
    z-index: 99;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    cursor: pointer;
`
